import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  Table,
  CustomInput,
  Button,
  Card,
} from "reactstrap";
import { useApolloClient } from "@apollo/react-hooks";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPencilAlt } from "@fortawesome/free-solid-svg-icons";

import API from "../../../api/API";
import {
  EDIT_PROJECT_ROLES,
  EDIT_WORKFLOWS_ROLES,
  GET_PROJECT_KEYS,
  CREATE_PROJECT_KEY,
  EDIT_PROJECT_KEY,
  DELETE_PROJECT_KEY,
} from "../../../api/projects";
import Loading from "../../atoms/loading";
import T from "../../atoms/translate";
import { t, showToast } from "../../../utils/utils";
import ProjectRoleCard from "../../molecules/project-role";
import ConfirmationModal from "../confirmation-modal";
import { PROJECT_SHIELD_API_KEY } from "../../../constants";

const WorkflowsConfig = ({ workflows, roles, confirmChanges, saving }) => {
  const [workflowRoles, setWorkflowRoles] = useState(workflows);
  return (
    <>
      <Col sm="12">
        <Alert color="info">
          <p className="mb-1 ss-fw-500 font-size-09">
            <T i18nKey="descripcionRolesPorWorkflow" />
          </p>
        </Alert>
      </Col>
      <Col xs="12">
        <Table responsive bordered>
          <thead>
            <tr>
              <th>Workflow</th>
              <th>
                <T i18nKey="protegidoPor" />
              </th>
              {roles.map((role) => (
                <th key={`rol-${role}`}>{role}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {workflowRoles.map((workflow) => (
              <tr key={`workflow-${workflow.id}`}>
                <td>{workflow.slug}</td>
                <td>
                  <CustomInput
                    type="switch"
                    id={`switch-${workflow.id}_shield_apikey`}
                    name="customSwitch"
                    label={
                      Array.isArray(workflow.protectedby) &&
                      workflow.protectedby.some(
                        (shield) => shield === PROJECT_SHIELD_API_KEY
                      )
                        ? `API Key: ${t("protegido")}`
                        : `API Key: ${t("noProtegido")}`
                    }
                    checked={
                      Array.isArray(workflow.protectedby) &&
                      workflow.protectedby.some(
                        (shield) => shield === PROJECT_SHIELD_API_KEY
                      )
                    }
                    value={workflow.id}
                    onChange={(event) => {
                      if (event.target && event.target.checked) {
                        //agregar a la protección
                        setWorkflowRoles(
                          workflowRoles.map((workflowRole) => {
                            if (workflowRole.id === workflow.id) {
                              return {
                                ...workflowRole,
                                protectedby: Array.isArray(
                                  workflowRole.protectedby
                                )
                                  ? [
                                      ...workflowRole.protectedby,
                                      PROJECT_SHIELD_API_KEY,
                                    ]
                                  : [PROJECT_SHIELD_API_KEY],
                              };
                            } else {
                              return workflowRole;
                            }
                          })
                        );
                      } else {
                        //quitar de la proteccion
                        setWorkflowRoles(
                          workflowRoles.map((workflowRole) => {
                            if (workflowRole.id === workflow.id) {
                              return {
                                ...workflowRole,
                                protectedby: Array.isArray(
                                  workflowRole.protectedby
                                )
                                  ? workflowRole.protectedby.filter(
                                      (shield) =>
                                        shield !== PROJECT_SHIELD_API_KEY
                                    )
                                  : [],
                              };
                            } else {
                              return workflowRole;
                            }
                          })
                        );
                      }
                    }}
                  />
                </td>
                {roles.map((role) => (
                  <td key={`workflow-${workflow.id}_rol-${role}`}>
                    <CustomInput
                      type="switch"
                      id={`switch-${workflow.id}_rol-${role}`}
                      name="customSwitch"
                      label={
                        Array.isArray(workflow.unauthorized_roles) &&
                        workflow.unauthorized_roles.some(
                          (unauthorizedRole) => unauthorizedRole === role
                        )
                          ? t("noAutorizado")
                          : t("autorizado")
                      }
                      checked={
                        !workflow.unauthorized_roles ||
                        !workflow.unauthorized_roles.some(
                          (unauthorizedRole) => unauthorizedRole === role
                        )
                      }
                      disabled={
                        !Array.isArray(workflow.protectedby) ||
                        workflow.protectedby.length === 0
                      }
                      value={workflow.id}
                      onChange={(event) => {
                        if (event.target && event.target.checked) {
                          //quitar de los no autorizados
                          setWorkflowRoles(
                            workflowRoles.map((workflowRole) => {
                              if (workflowRole.id === workflow.id) {
                                const newUnathorizedRoles = Array.isArray(
                                  workflowRole.unauthorized_roles
                                )
                                  ? workflowRole.unauthorized_roles.filter(
                                      (r) => r !== role
                                    )
                                  : [];
                                return {
                                  ...workflowRole,
                                  unauthorized_roles: newUnathorizedRoles,
                                };
                              } else {
                                return workflowRole;
                              }
                            })
                          );
                        } else {
                          //agregar a los no autorizados
                          setWorkflowRoles(
                            workflowRoles.map((workflowRole) => {
                              if (workflowRole.id === workflow.id) {
                                const newUnathorizedRoles = Array.isArray(
                                  workflowRole.unauthorized_roles
                                )
                                  ? [...workflowRole.unauthorized_roles, role]
                                  : [role];
                                return {
                                  ...workflowRole,
                                  unauthorized_roles: newUnathorizedRoles,
                                };
                              } else {
                                return workflowRole;
                              }
                            })
                          );
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Col>
      <Col xs="12" className="text-center">
        <Button
          color="primary"
          onClick={(e) => confirmChanges(workflowRoles)}
          disabled={saving}
        >
          {saving ? (
            <T i18nKey="guardando..." />
          ) : (
            <T i18nKey="savePermissionsChanges" />
          )}
        </Button>
      </Col>
    </>
  );
};

const KeysConfig = ({ projectId, projectRoles }) => {
  const client = useApolloClient();
  const [projectKeys, setProjectKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [keyChanging, setKeyChanging] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    async function getKeys() {
      try {
        const response = await API.query(client, GET_PROJECT_KEYS, {
          projectId: projectId,
        });
        if (response && response.data && response.data.getProjectKeys) {
          setProjectKeys(response.data.getProjectKeys);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    getKeys();
  }, [client, projectId]);

  const createKey = async () => {
    try {
      setCreating(true);
      const response = await API.mutation(client, CREATE_PROJECT_KEY, {
        projectId: projectId,
        roles: [],
        domains: [],
        enviroments: [],
      });
      if (response && response.data && response.data.createKey) {
        setProjectKeys([...projectKeys, response.data.createKey]);
        showToast(t("crearKeyMensajeExito"));
      } else {
        showToast(t("crearKeyMensajeError"), "error");
      }
    } catch (error) {
      console.log(error);
      showToast(t("crearKeyMensajeError"), "error");
    } finally {
      setCreating(false);
    }
  };

  const editKey = async (id, roles, domains, enviroments) => {
    setProjectKeys(
      projectKeys.map((key) => {
        if (key.id === id) {
          return { ...key, roles, whitelist: domains, enviroments };
        }
        return key;
      })
    );
  };

  const confirmChanges = async (id) => {
    try {
      setUpdating(true);
      const key = projectKeys.find((key) => key.id === id);
      const response = await API.mutation(client, EDIT_PROJECT_KEY, {
        id: id,
        roles: key.roles,
        domains: key.whitelist,
        enviroments: key.enviroments,
      });
      if (response && response.data && response.data.editKey) {
        showToast(t("editarKeyMensajeExito"));
        setKeyChanging(null);
      } else {
        showToast(t("editarKeyMensajeError"), "error");
      }
    } catch (error) {
      console.log(error);
      showToast(t("editarKeyMensajeError"), "error");
    } finally {
      setUpdating(false);
    }
  };

  const deleteKey = async () => {
    try {
      setUpdating(true);
      const response = await API.mutation(client, DELETE_PROJECT_KEY, {
        id: confirmDelete,
      });
      if (response && response.data && response.data.deleteKey) {
        setConfirmDelete(null);
        setProjectKeys(projectKeys.filter((key) => key.id !== confirmDelete));
        showToast(t("eliminarKeyMensajeExito"));
      } else {
        showToast(t("eliminarKeyMensajeError"), "error");
      }
    } catch (error) {
      console.log(error);
      showToast(t("eliminarKeyMensajeError"), "error");
    } finally {
      setUpdating(false);
    }
  };

  if (loading)
    return (
      <Col xs="12">
        <Loading width={300} height={300} />
      </Col>
    );
  return (
    <>
      <Col xs="12">
        <Button
          color="primary"
          className="fw-500"
          onClick={createKey}
          disabled={creating || updating}
        >
          <T i18nKey={creating ? "procesando..." : "crearKey"} />
        </Button>
      </Col>
      <Col xs="12" className="mt-3">
        <Card body className="border-0 box-shadow mb-4">
          <Row>
            <Col xs="12">
              <h1 className="font-size-09 text-secondary fw-600 text-uppercase">
                <T i18nKey="keysExistentesProyecto" />
              </h1>
            </Col>
            <Col xs="12" className="mt-3">
              {Array.isArray(projectKeys) && projectKeys.length > 0 ? (
                <Table responsive bordered>
                  <thead>
                    <tr>
                      <th>API Key</th>
                      <th>
                        <T i18nKey="entornos" />
                      </th>
                      {Array.isArray(projectRoles) && projectRoles.map((role) => (
                        <th key={`rol-${role}`}>{role}</th>
                      ))}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectKeys.map((projectKey) => (
                      <tr key={`key-${projectKey.id}`}>
                        <td>{projectKey.key}</td>
                        <td>
                          <CustomInput
                            type="switch"
                            id={`switch-${projectKey.id}_develop`}
                            name="customSwitch"
                            label={
                              Array.isArray(projectKey.enviroments) &&
                              projectKey.enviroments.some(
                                (env) => env === "develop"
                              )
                                ? `Develop: ${t("autorizado")}`
                                : `Develop: ${t("noAutorizado")}`
                            }
                            checked={
                              Array.isArray(projectKey.enviroments) &&
                              projectKey.enviroments.some(
                                (env) => env === "develop"
                              )
                            }
                            value={projectKey.id}
                            onChange={(event) => {
                              if (event.target && event.target.checked) {
                                //añadir en entorno a autorizado
                                editKey(
                                  projectKey.id,
                                  [...projectKey.roles],
                                  [...projectKey.whitelist],
                                  [...projectKey.enviroments, "develop"]
                                );
                              } else {
                                //quitar de los autorizados
                                editKey(
                                  projectKey.id,
                                  [...projectKey.roles],
                                  [...projectKey.whitelist],
                                  projectKey.enviroments.filter(
                                    (key) => key !== "develop"
                                  )
                                );
                              }
                            }}
                            disabled={keyChanging !== projectKey.id}
                          />

                          <CustomInput
                            type="switch"
                            id={`switch-${projectKey.id}_master`}
                            name="customSwitch"
                            label={
                              Array.isArray(projectKey.enviroments) &&
                              projectKey.enviroments.some(
                                (env) => env === "master"
                              )
                                ? `Master: ${t("autorizado")}`
                                : `Master: ${t("noAutorizado")}`
                            }
                            checked={
                              Array.isArray(projectKey.enviroments) &&
                              projectKey.enviroments.some(
                                (env) => env === "master"
                              )
                            }
                            value={projectKey.id}
                            onChange={(event) => {
                              if (event.target && event.target.checked) {
                                //añadir en entorno a autorizado
                                editKey(
                                  projectKey.id,
                                  [...projectKey.roles],
                                  [...projectKey.whitelist],
                                  [...projectKey.enviroments, "master"]
                                );
                              } else {
                                //quitar de los autorizados
                                editKey(
                                  projectKey.id,
                                  [...projectKey.roles],
                                  [...projectKey.whitelist],
                                  projectKey.enviroments.filter(
                                    (key) => key !== "master"
                                  )
                                );
                              }
                            }}
                            disabled={keyChanging !== projectKey.id}
                          />
                        </td>
                        {Array.isArray(projectRoles) && projectRoles.map((role) => (
                          <td key={`key-${projectKey.id}_rol-${role}`}>
                            <CustomInput
                              type="switch"
                              id={`switch-${projectKey.id}_rol-${role}`}
                              name="customSwitch"
                              label={
                                Array.isArray(projectKey.roles) &&
                                projectKey.roles.some((r) => r === role)
                                  ? t("autorizado")
                                  : t("noAutorizado")
                              }
                              checked={
                                Array.isArray(projectKey.roles) &&
                                projectKey.roles.some((r) => r === role)
                              }
                              value={projectKey.id}
                              onChange={(event) => {
                                if (event.target && event.target.checked) {
                                  //añadir en entorno a autorizado
                                  editKey(
                                    projectKey.id,
                                    [...projectKey.roles, role],
                                    [...projectKey.whitelist],
                                    [...projectKey.enviroments]
                                  );
                                } else {
                                  //quitar de los autorizados
                                  editKey(
                                    projectKey.id,
                                    [
                                      projectKey.roles.filter(
                                        (key) => key !== role
                                      ),
                                    ],
                                    [...projectKey.whitelist],
                                    [...projectKey.enviroments]
                                  );
                                }
                              }}
                              disabled={keyChanging !== projectKey.id}
                            />
                          </td>
                        ))}
                        <td>
                          <Button
                            size="sm"
                            color={
                              keyChanging === projectKey.id ? "primary" : "info"
                            }
                            onClick={(e) => {
                              if (keyChanging === projectKey.id) {
                                confirmChanges(projectKey.id);
                              } else {
                                setKeyChanging(projectKey.id);
                              }
                            }}
                            disabled={creating || updating}
                          >
                            {keyChanging === projectKey.id ? (
                              updating ? (
                                <T i18nKey="procesando..." />
                              ) : (
                                <T i18nKey="actualizar" />
                              )
                            ) : (
                              <FontAwesomeIcon icon={faPencilAlt} />
                            )}
                          </Button>{" "}
                          <Button
                            size="sm"
                            color="danger"
                            onClick={(e) => setConfirmDelete(projectKey.id)}
                            disabled={creating || updating}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert color="info">
                  <p className="mb-1 ss-fw-500 font-size-09">
                    <T i18nKey="proyectoNoTieneKeys" />
                  </p>
                </Alert>
              )}
            </Col>
          </Row>
        </Card>
        <ConfirmationModal
          isOpen={confirmDelete !== null}
          title={t("confirmeEliminacionKey")}
          closeButtonText={t("cancelar")}
          closeAction={() => setConfirmDelete(null)}
          primaryButtonText={t("confirmar")}
          primaryAction={deleteKey}
        >
          <p>
            <T i18nKey="mensajeEliminacionKey" />
          </p>
        </ConfirmationModal>
      </Col>
    </>
  );
};

const ProjectConfig = ({
  projectId,
  projectRoles,
  setProjectRoles,
  workflows,
  setWorkflows,
}) => {
  const client = useApolloClient();
  const [activeTab, setActiveTab] = useState("roles");
  const [saving, setSaving] = useState(false);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const addRole = (role) => {
    if(Array.isArray(projectRoles)){
      saveRole([...projectRoles, role], t("agregarRolMensajeExito"));
    }else{
      saveRole([role], t("agregarRolMensajeExito"));
    }
  };

  const deleteRole = async (roleToDelete) => {
    saveRole(
      projectRoles.filter((role) => role !== roleToDelete),
      t("eliminarRolMensajeExito")
    );
  };

  const saveRole = async (roles, msg) => {
    setProjectRoles(roles);
    try {
      const res = await API.mutation(client, EDIT_PROJECT_ROLES, {
        id: projectId,
        roles: roles,
      });
      if (
        res &&
        res.data &&
        res.data.editProjectRoles &&
        res.data.editProjectRoles.id
      ) {
        showToast(msg);
      }
    } catch (error) {
      console.log(error);
      showToast(t("actualizarRolesProyectoMensajeError"), "error");
    }
  };

  const saveWorkflowRoles = async (modifiedWorkflows) => {
    try {
      setSaving(true);
      const res = await API.mutation(client, EDIT_WORKFLOWS_ROLES, {
        workflows: modifiedWorkflows.map((modifiedWorkflow) => {
          return {
            id: modifiedWorkflow.id,
            roles: modifiedWorkflow.unauthorized_roles,
            protectedby: modifiedWorkflow.protectedby,
          };
        }),
      });
      if (res && res.data && res.data.editWorkflowsRoles) {
        showToast(t("actualizarRolesWorkflowsMensajeExito"));
        setWorkflows(modifiedWorkflows);
      }
    } catch (error) {
      showToast(t("actualizarRolesWorkflowsMensajeError"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Row>
      <Col xs="12" className="px-4">
        <Nav tabs>
          <NavItem>
            <NavLink
              className={`cursor-pointer ${
                activeTab === "roles" ? "active" : ""
              }`}
              onClick={() => {
                toggle("roles");
              }}
            >
              <T i18nKey="roles" />
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`cursor-pointer ${
                activeTab === "permissions" ? "active" : ""
              }`}
              onClick={() => {
                toggle("permissions");
              }}
            >
              <T i18nKey="permisos" />
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`cursor-pointer ${
                activeTab === "keys" ? "active" : ""
              }`}
              onClick={() => {
                toggle("keys");
              }}
            >
              <T i18nKey="clavesAPI" />
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="roles">
            <Row>
              <Col xs="12" className="mt-3">
                <Row>
                  <ProjectRoleCard
                    newRole
                    createRole={(role) => addRole(role)}
                    currentRoles={projectRoles}
                  />
                  {Array.isArray(projectRoles) && projectRoles.map((role) => (
                    <ProjectRoleCard
                      key={`role-${role}`}
                      name={role}
                      deleteRole={() => deleteRole(role)}
                    />
                  ))}
                </Row>
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="permissions" className="py-3">
            <Row>
              {!Array.isArray(projectRoles) || projectRoles.length === 0 ? (
                <Col sm="12">
                  <Alert color="info">
                    <p className="mb-1 ss-fw-500 font-size-09">
                      <T i18nKey="avisoNoExistenRoles" />
                    </p>
                  </Alert>
                </Col>
              ) : (
                <WorkflowsConfig
                  workflows={workflows}
                  roles={projectRoles}
                  confirmChanges={(modifiedWorkflows) =>
                    saveWorkflowRoles(modifiedWorkflows)
                  }
                  saving={saving}
                />
              )}
            </Row>
          </TabPane>
          <TabPane tabId="keys" className="py-3">
            <Row>
              <KeysConfig projectId={projectId} projectRoles={projectRoles} />
            </Row>
          </TabPane>
        </TabContent>
      </Col>
    </Row>
  );
};

export default ProjectConfig;
